/**
 * Constant-acceleration kinematics in one dimension — the "SUVAT" equations.
 *
 * Five variables — displacement s, initial velocity u, final velocity v, acceleration a
 * and time t — are tied together by five equations, only two of which are independent:
 *
 *   v = u + at            s = ut + ½at²          s = vt − ½at²
 *   s = ½(u + v)t         v² = u² + 2as
 *
 * Any three of the five variables fix the other two (OpenStax University Physics Vol. 1,
 * §3.4 "Motion with Constant Acceleration"). `solveKinematics` finds the other two for
 * each of the ten possible triples and records which rearranged equation produced each
 * value, so the widget can show its working.
 *
 * Signs follow the usual convention: one direction is positive, and s, u, v and a are
 * signed along it. Time never runs backwards, so a root with t < 0 is discarded.
 */

export type KinVar = "s" | "u" | "v" | "a" | "t";
export const KIN_VARS: readonly KinVar[] = ["s", "u", "v", "a", "t"];

export type KinValues = Record<KinVar, number>;
export type KinKnown = Partial<Record<KinVar, number>>;

export type RuleId =
  | "a=(v-u)/t"
  | "v=u+at"
  | "u=v-at"
  | "t=(v-u)/a"
  | "s=(u+v)t/2"
  | "s=ut+at2/2"
  | "s=vt-at2/2"
  | "a=(v2-u2)/2s"
  | "t=2s/(u+v)"
  | "v=sqrt(u2+2as)"
  | "u=sqrt(v2-2as)"
  | "s=(v2-u2)/2a"
  | "a=2(s-ut)/t2"
  | "a=2(vt-s)/t2"
  | "u=2s/t-v"
  | "u=s/t-at/2"
  | "v=s/t+at/2"
  | "t=(-u+sqrt)/a"
  | "t=(v-sqrt)/a"
  | "t=s/u"
  | "t=s/v"
  | "v=u"
  | "u=v";

export interface Rule {
  /** The variable the rule finds. */
  target: KinVar;
  /** The textbook equation the rule is rearranged from. */
  equation: string;
  /** The rearranged right-hand side, written with the variable letters s, u, v, a, t. */
  rhs: string;
}

export const RULES: Record<RuleId, Rule> = {
  "a=(v-u)/t": { target: "a", equation: "v = u + at", rhs: "(v − u) / t" },
  "v=u+at": { target: "v", equation: "v = u + at", rhs: "u + a × t" },
  "u=v-at": { target: "u", equation: "v = u + at", rhs: "v − a × t" },
  "t=(v-u)/a": { target: "t", equation: "v = u + at", rhs: "(v − u) / a" },
  "s=(u+v)t/2": {
    target: "s",
    equation: "s = ½(u + v)t",
    rhs: "(u + v) × t / 2",
  },
  "s=ut+at2/2": {
    target: "s",
    equation: "s = ut + ½at²",
    rhs: "u × t + a × t² / 2",
  },
  "s=vt-at2/2": {
    target: "s",
    equation: "s = vt − ½at²",
    rhs: "v × t − a × t² / 2",
  },
  "a=(v2-u2)/2s": {
    target: "a",
    equation: "v² = u² + 2as",
    rhs: "(v² − u²) / (2 × s)",
  },
  "t=2s/(u+v)": {
    target: "t",
    equation: "s = ½(u + v)t",
    rhs: "2 × s / (u + v)",
  },
  "v=sqrt(u2+2as)": {
    target: "v",
    equation: "v² = u² + 2as",
    rhs: "±√(u² + 2 × a × s)",
  },
  "u=sqrt(v2-2as)": {
    target: "u",
    equation: "v² = u² + 2as",
    rhs: "±√(v² − 2 × a × s)",
  },
  "s=(v2-u2)/2a": {
    target: "s",
    equation: "v² = u² + 2as",
    rhs: "(v² − u²) / (2 × a)",
  },
  "a=2(s-ut)/t2": {
    target: "a",
    equation: "s = ut + ½at²",
    rhs: "2 × (s − u × t) / t²",
  },
  "a=2(vt-s)/t2": {
    target: "a",
    equation: "s = vt − ½at²",
    rhs: "2 × (v × t − s) / t²",
  },
  "u=2s/t-v": { target: "u", equation: "s = ½(u + v)t", rhs: "2 × s / t − v" },
  "u=s/t-at/2": {
    target: "u",
    equation: "s = ut + ½at²",
    rhs: "s / t − a × t / 2",
  },
  "v=s/t+at/2": {
    target: "v",
    equation: "s = vt − ½at²",
    rhs: "s / t + a × t / 2",
  },
  "t=(-u+sqrt)/a": {
    target: "t",
    equation: "s = ut + ½at²",
    rhs: "(−u ± √(u² + 2 × a × s)) / a",
  },
  "t=(v-sqrt)/a": {
    target: "t",
    equation: "s = vt − ½at²",
    rhs: "(v ± √(v² − 2 × a × s)) / a",
  },
  "t=s/u": { target: "t", equation: "s = ut", rhs: "s / u" },
  "t=s/v": { target: "t", equation: "s = vt", rhs: "s / v" },
  "v=u": { target: "v", equation: "v = u + at (a = 0)", rhs: "u" },
  "u=v": { target: "u", equation: "v = u + at (a = 0)", rhs: "v" },
};

export interface KinStep {
  rule: RuleId;
  /** For a ± rule: which root this solution uses. */
  sign?: 1 | -1;
}

export interface KinSolution {
  values: KinValues;
  steps: KinStep[];
}

export type KinError =
  | "need-three"
  | "too-many"
  | "negative-time"
  | "negative-time-input"
  | "zero-time"
  | "no-solution"
  | "undetermined"
  | "not-finite";

export type KinResult =
  | { ok: true; solutions: KinSolution[]; discardedNegativeTime: boolean }
  | { ok: false; error: KinError };

const FAIL = (error: KinError): KinResult => ({ ok: false, error });

function pass(
  values: KinValues,
  steps: KinStep[],
  discardedNegativeTime = false
): KinResult {
  return {
    ok: true,
    solutions: [{ values, steps }],
    discardedNegativeTime,
  };
}

/** A square root that forgives the last bit of floating-point noise at exactly zero. */
function safeRoot(radicand: number, scale: number): number | null {
  if (radicand >= 0) {
    return Math.sqrt(radicand);
  }
  return radicand > -1e-12 * Math.max(1, scale) ? 0 : null;
}

type Solver = (k: Required<KinKnown>) => KinResult;

const solveUVT: Solver = ({ u, v, t }) => {
  if (t === 0) {
    return FAIL("zero-time");
  }
  const a = (v - u) / t;
  const s = ((u + v) * t) / 2;
  return pass({ s, u, v, a, t }, [
    { rule: "a=(v-u)/t" },
    { rule: "s=(u+v)t/2" },
  ]);
};

const solveUAT: Solver = ({ u, a, t }) =>
  pass({ s: u * t + (a * t * t) / 2, u, v: u + a * t, a, t }, [
    { rule: "v=u+at" },
    { rule: "s=ut+at2/2" },
  ]);

const solveVAT: Solver = ({ v, a, t }) =>
  pass({ s: v * t - (a * t * t) / 2, u: v - a * t, v, a, t }, [
    { rule: "u=v-at" },
    { rule: "s=vt-at2/2" },
  ]);

const solveSUV: Solver = ({ s, u, v }) => {
  if (s === 0) {
    return u === v || u + v === 0 ? FAIL("undetermined") : FAIL("no-solution");
  }
  if (u + v === 0) {
    return FAIL("no-solution");
  }
  const t = (2 * s) / (u + v);
  if (t < 0) {
    return FAIL("no-solution");
  }
  const a = (v * v - u * u) / (2 * s);
  return pass({ s, u, v, a, t }, [
    { rule: "t=2s/(u+v)" },
    { rule: "a=(v2-u2)/2s" },
  ]);
};

const solveSUT: Solver = ({ s, u, t }) => {
  if (t === 0) {
    return FAIL("zero-time");
  }
  const a = (2 * (s - u * t)) / (t * t);
  return pass({ s, u, v: u + a * t, a, t }, [
    { rule: "a=2(s-ut)/t2" },
    { rule: "v=u+at" },
  ]);
};

const solveSVT: Solver = ({ s, v, t }) => {
  if (t === 0) {
    return FAIL("zero-time");
  }
  const a = (2 * (v * t - s)) / (t * t);
  return pass({ s, u: v - a * t, v, a, t }, [
    { rule: "a=2(vt-s)/t2" },
    { rule: "u=v-at" },
  ]);
};

const solveSAT: Solver = ({ s, a, t }) => {
  if (t === 0) {
    return FAIL("zero-time");
  }
  const u = s / t - (a * t) / 2;
  return pass({ s, u, v: s / t + (a * t) / 2, a, t }, [
    { rule: "u=s/t-at/2" },
    { rule: "v=s/t+at/2" },
  ]);
};

const solveUVA: Solver = ({ u, v, a }) => {
  if (a === 0) {
    return u === v ? FAIL("undetermined") : FAIL("no-solution");
  }
  const t = (v - u) / a;
  if (t < 0) {
    return FAIL("no-solution");
  }
  return pass({ s: (v * v - u * u) / (2 * a), u, v, a, t }, [
    { rule: "t=(v-u)/a" },
    { rule: "s=(v2-u2)/2a" },
  ]);
};

/** The time roots of ½at² + bt + c = 0 style problems, shared by the two s,a cases. */
interface RootCase {
  s: number;
  a: number;
  /** The known velocity (u or v) and which it is. */
  known: number;
  knownIs: "u" | "v";
}

function solveWithRoots({ s, a, known, knownIs }: RootCase): KinResult {
  const u = knownIs === "u";
  const sign = u ? -1 : 1;
  if (a === 0) {
    if (known === 0) {
      return s === 0 ? FAIL("undetermined") : FAIL("no-solution");
    }
    const t = s / known;
    if (t < 0) {
      return FAIL("no-solution");
    }
    return pass({ s, u: known, v: known, a, t }, [
      { rule: u ? "t=s/u" : "t=s/v" },
      { rule: u ? "v=u" : "u=v" },
    ]);
  }
  const radicand = u ? known * known + 2 * a * s : known * known - 2 * a * s;
  const root = safeRoot(radicand, known * known + Math.abs(2 * a * s));
  if (root === null) {
    return FAIL("no-solution");
  }
  const candidates: { t: number; sign: 1 | -1 }[] = [
    { t: (sign * known + root) / a, sign: 1 },
    { t: (sign * known - root) / a, sign: -1 },
  ];
  // A zero discriminant gives one repeated root, not two solutions.
  const distinct = root === 0 ? candidates.slice(0, 1) : candidates;
  const forward = distinct.filter((c) => c.t >= 0);
  const discarded = forward.length < distinct.length;
  // t = 0 is the trivial "still at the start" root; keep it only if nothing else exists.
  const nonTrivial = forward.filter((c) => c.t > 0);
  const kept = nonTrivial.length > 0 ? nonTrivial : forward;
  if (kept.length === 0) {
    return FAIL("negative-time");
  }
  const solutions: KinSolution[] = kept.map((c) => {
    const other = u ? known + a * c.t : known - a * c.t;
    const values: KinValues = u
      ? { s, u: known, v: other, a, t: c.t }
      : { s, u: other, v: known, a, t: c.t };
    return {
      values,
      steps: [
        { rule: u ? "t=(-u+sqrt)/a" : "t=(v-sqrt)/a", sign: c.sign },
        { rule: u ? "v=u+at" : "u=v-at" },
      ],
    };
  });
  return { ok: true, solutions, discardedNegativeTime: discarded };
}

const solveSUA: Solver = ({ s, u, a }) =>
  solveWithRoots({ s, a, known: u, knownIs: "u" });
const solveSVA: Solver = ({ s, v, a }) =>
  solveWithRoots({ s, a, known: v, knownIs: "v" });

const SOLVERS: Record<string, Solver> = {
  "u,v,t": solveUVT,
  "u,a,t": solveUAT,
  "v,a,t": solveVAT,
  "s,u,v": solveSUV,
  "s,u,t": solveSUT,
  "s,v,t": solveSVT,
  "s,a,t": solveSAT,
  "u,v,a": solveUVA,
  "s,u,a": solveSUA,
  "s,v,a": solveSVA,
};

/** The order the solvers' keys are written in. */
const KEY_ORDER: readonly KinVar[] = ["s", "u", "v", "a", "t"];

function everyFinite(solutions: KinSolution[]): boolean {
  return solutions.every((solution) =>
    KIN_VARS.every((name) => Number.isFinite(solution.values[name]))
  );
}

/** Solve for the two unknowns given exactly three of s, u, v, a, t. */
export function solveKinematics(known: KinKnown): KinResult {
  const names = KEY_ORDER.filter((name) => known[name] !== undefined);
  if (names.length < 3) {
    return FAIL("need-three");
  }
  if (names.length > 3) {
    return FAIL("too-many");
  }
  if ((known.t ?? 0) < 0) {
    return FAIL("negative-time-input");
  }
  // Order the key as the solver table writes it: s before u before v before a before t.
  const key = names
    .slice()
    .sort((x, y) => KEY_ORDER.indexOf(x) - KEY_ORDER.indexOf(y))
    .join(",");
  const solver = SOLVERS[key];
  if (!solver) {
    return FAIL("need-three");
  }
  const result = solver(known as Required<KinKnown>);
  if (result.ok && !everyFinite(result.solutions)) {
    return FAIL("not-finite");
  }
  return result;
}

/**
 * The right-hand side of a rule with the variables replaced by the solution's values.
 * `show` formats one number; negatives are bracketed so −3² reads (−3)².
 */
const VARIABLE_LETTER = /[suvat]/g;
const PLUS_MINUS = "±";

export function substituteRule(
  rule: Rule,
  values: KinValues,
  sign: 1 | -1 | undefined,
  show: (value: number) => string
): string {
  const replaced = rule.rhs.replace(VARIABLE_LETTER, (letter) => {
    const text = show(values[letter as KinVar]);
    return text.startsWith("-") || text.startsWith("−") ? `(${text})` : text;
  });
  if (sign === undefined) {
    return replaced;
  }
  return replaced.replaceAll(PLUS_MINUS, sign === 1 ? "+" : "−");
}
